import React, {
  createContext, useContext, useState, useLayoutEffect
} from 'react';
import { connect } from 'react-redux';
import { find } from 'lodash';
import runPreload from '../logic/preload';
import Forward from '../logic/forward';
import saveUserData from '../logic/save_user_data';
import PageLoader from '../../common/components/page_loader';
import Intro from '../../common/components/intro';
import Screening from '../../common/components/screening';
import Qualified from '../../common/components/qualified';
import CustomQualified from '../../common/components/custom_qualified';
import Preference from '../../common/components/metrics/preference';
import PreferenceReasons from '../../common/components/metrics/preference_reasons';
import renderRawHtml from '../../../../common/render_raw_html';
import { fraudContext } from "../../common/contexts";
import { LastStepsComponents } from '../../common/logic/last_steps';
import { customMetricsByLocation } from "../../common/logic/eligible";
import WithOpenEnd from '../../common/components/metrics/customMetricsOpenEnd/withOpenEnd';
import Concept from './concept';

const resultContext = createContext(
  {
    answers: [],
    loops: [],
    preference_views: [],
    preference_selections: [],
    preference_answers: [],
    thought_bubbles: [],
    video: {}
  }
);

const Index = ({
  updateStep, updateConceptStep,
  updateConceptIndex, updateQuestionIndex,
  data, cIndex, qIndex, step, loading, conceptStep,
  changeLoadingState
}) => {
  const {
    qualified_custom_text, monadic, concepts,
    demographics, optional_demographics, screenings,
    translations, concepts_for_preference, preference_all
  } = data;

  const preferenceTitle = preference_all ?
    translations.preference_all_title :
    translations.preference_title;

  const result = useContext(resultContext);
  const { projectId, antiFraud, fraudProcessing, gaTrackingId } = useContext(fraudContext);

  useLayoutEffect(() => {
    runPreload(data, [ ...concepts, ...concepts_for_preference ], [ 'url' ], changeLoadingState);
  }, []);

  const saveRequest = (status) => {
    saveUserData(data.save_url, status, result, data);
  };
  const currentConcept = () => (concepts[cIndex]);
  const currentQuestion = (questions) => (questions[qIndex]);

  const [ forwardEntity ] = useState(new Forward({
    updateStep,
    runSave: (status) => saveRequest(status),
    updateConceptStep,
    updateConceptIndex,
    updateQuestionIndex
  }));
  const goForward = () => {
    forwardEntity.forward(step, data, result, goNextConcept, projectId, antiFraud, gaTrackingId);
  };
  const goNextQuestion = (questions) => {
    forwardEntity.nextQuestion(
      qIndex, questions, result, goForward
    );
  };
  const goNextConcept = () => {
    forwardEntity.nextConceptStep(
      conceptStep, cIndex, currentConcept(), data, goForward
    );
  };

  const preferenceSelectedConcept = () => {
    const selection = result.preference_selections[0];
    if (!selection) {
      return null;
    }
    return find(concepts_for_preference, (c) => c.id === parseInt(selection.concept_id));
  };

  return (
    <>
      <PageLoader loading={ loading || fraudProcessing } />
      <section className="survey-layout">
        {
          !step &&
          <Intro
            task={ translations.base_intro }
            nextStep={ goForward }
          />
        }
        {
          step === 1 &&
          <Screening
            question={ currentQuestion(demographics) }
            nextStep={ () => { goNextQuestion(demographics); } }
            result={ result }
          />
        }
        {
          step === 2 &&
          <Screening
            question={ currentQuestion(optional_demographics) }
            nextStep={ () => { goNextQuestion(optional_demographics); } }
            result={ result }
          />
        }
        {
          step === 3 &&
          <Screening
            question={ currentQuestion(screenings) }
            nextStep={ () => { goNextQuestion(screenings); } }
            result={ result }
          />
        }
        {
          step === 4 && !qualified_custom_text &&
          <Qualified
            title={ translations.express_qualify_title }
            description={ translations.express_qualify_description }
            descriptionBottom={ translations.express_qualify_footer }
            nextStep={ goForward }
          />
        }
        {
          step === 4 && !!qualified_custom_text &&
          <CustomQualified
            content={ qualified_custom_text }
            nextStep={ goForward }
          />
        }
        {
          step === 5 &&
          <WithOpenEnd
            question={ currentQuestion(customMetricsByLocation(data.survey_metrics, "at_the_start")) }
            nextStep={ () => { goNextQuestion(customMetricsByLocation(data.survey_metrics, "at_the_start")); } }
            result={ result }
            mainResult={ result }
          />
        }
        {
          step === 6 &&
          <Concept
            conceptIndex={ cIndex }
            concept={ currentConcept() }
            data={ data }
            nextStep={ () => { goNextConcept(); } }
            monadic={ monadic }
            index={ conceptStep }
            result={ result }
          />
        }
        {
          step === 7 &&
          <WithOpenEnd
            question={ currentQuestion(customMetricsByLocation(data.survey_metrics, "default")) }
            nextStep={ () => { goNextQuestion(customMetricsByLocation(data.survey_metrics, "default")); } }
            result={ result }
            mainResult={ result }
          />
        }
        {
          step === 8 &&
          <Preference
            title={ preferenceTitle }
            concepts={ concepts_for_preference }
            result={ result }
            nextStep={ goForward }
            bigger
          />
        }
        {
          step === 9 &&
          <PreferenceReasons
            concept={ preferenceSelectedConcept() }
            result={ result }
            note={ translations.reasons_for_preference_note }
            placeholder={ translations.reasons_for_preference_placeholder }
            nextStep={ goForward }
            forceImage
          >
            <div className="survey-header">
              <div className="survey-header_title">
                <div
                  className="survey-start_text -low-margin-bottom"
                  { ...renderRawHtml(translations.reasons_for_preference_title) }
                />
              </div>
            </div>
          </PreferenceReasons>
        }
        {
          step === 10 &&
          <WithOpenEnd
            question={ currentQuestion(customMetricsByLocation(data.survey_metrics, "at_the_end")) }
            nextStep={ () => { goNextQuestion(customMetricsByLocation(data.survey_metrics, "at_the_end")); } }
            result={ result }
            mainResult={ result }
          />
        }
        <LastStepsComponents
          step={ step }
          lastStepsFrom={ 11 }
          translations={ translations }
          data={ data }
          nextStep={ goForward }
        />
      </section>
    </>
  );
};

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => {
  return {
    updateStep: (step) => { return dispatch({ type: "UPDATE_STEP", value: step }); },
    changeLoadingState: (value) => { return dispatch({ type: "UPDATE_LOADING", value }); },
    updateConceptStep: (step) => { return dispatch({ type: "UPDATE_CONCEPT_STEP", value: step }); },
    updateConceptIndex: (cIndex) => { return dispatch({ type: "UPDATE_CONCEPT_INDEX", value: cIndex }); },
    updateQuestionIndex: (qIndex) => { return dispatch({ type: "UPDATE_QUESTION_INDEX", value: qIndex }); }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Index);
